import { api } from '@/config/api';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface FunnelResult {
  new: number;
  contacted: number;
  qualified: number;
  won: number;
  lost: number;
  total: number;
  conversionRate: number;
}

export interface CampaignRow {
  campaignId: string;
  campaignName: string;
  total: number;
  byStatus: { new: number; contacted: number; qualified: number; won: number; lost: number };
  dealValue: number;
  avgDealValue: number;
}

export interface AdRow {
  adId: string;
  adName: string;
  headline: string;
  mediaKind: string;
  total: number;
  byStatus: { new: number; contacted: number; qualified: number; won: number; lost: number };
  dealValue: number;
}

export interface TimingsResult {
  medianTimeToFirstReplyMs: number | null;
  medianTimeToQualifiedMs: number | null;
  medianTimeToWonMs: number | null;
  p90TimeToFirstReplyMs: number | null;
}

export interface AnalyticsFilter {
  from?: string;   // ISO date string
  to?: string;     // ISO date string
  campaignId?: string;
  source?: string;
}

// ─── Service ──────────────────────────────────────────────────────────────

export const leadsAnalyticsService = {
  getFunnel: async (filter: AnalyticsFilter = {}): Promise<FunnelResult> =>
    (await api.get('/slighter_crm/leads/analytics/funnel', { params: filter })).data,

  getByCampaign: async (filter: AnalyticsFilter = {}): Promise<CampaignRow[]> =>
    (await api.get('/slighter_crm/leads/analytics/by-campaign', { params: filter })).data,

  getByAd: async (filter: AnalyticsFilter = {}): Promise<AdRow[]> =>
    (await api.get('/slighter_crm/leads/analytics/by-ad', { params: filter })).data,

  getTimings: async (filter: AnalyticsFilter = {}): Promise<TimingsResult> =>
    (await api.get('/slighter_crm/leads/analytics/timings', { params: filter })).data,

  /**
   * Triggers a CSV download in the browser for the current filtered view.
   * Returns the object URL so the caller can revoke it after download.
   */
  exportCsv: async (filter: {
    status?: string;
    q?: string;
    source?: string;
    sourceCampaignId?: string;
    sourceAdId?: string;
    assignedToUserUid?: string;
    from?: string;
    to?: string;
  } = {}): Promise<void> => {
    const response = await api.get('/slighter_crm/leads/export.csv', {
      params: filter,
      responseType: 'blob',
    });
    const url = URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `leads-${Date.now()}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },
};
